// src/components/GiftCardSuccess.jsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Gift, CheckCircle, Mail, MapPin } from "lucide-react";

export default function GiftCardSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { giftCard, customization, giftCardCode } = location.state || {};

  if (!giftCard || !customization) {
    navigate("/gift-cards");
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4">
            <CheckCircle className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Gift Card Purchased!
          </h1>
          <p className="text-lg text-gray-600">
            Your gift card has been created successfully
          </p>
        </div>

        {/* Gift Card Details */}
        <Card className="mb-6 border-emerald-200">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-600" />
              Gift Card Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Gift Card Code */}
            <div className="p-6 bg-emerald-50 rounded-xl text-center border-2 border-emerald-200">
              <p className="text-sm text-emerald-700 mb-2">Gift Card Code</p>
              <p className="text-3xl font-bold text-emerald-900 font-mono tracking-wider">
                {giftCardCode || "GIFT-XXXX-XXXX"}
              </p>
              <p className="text-xs text-emerald-600 mt-2">
                Save this code for your records
              </p>
            </div>

            {/* Order Details */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="text-lg font-semibold text-gray-900">
                  ${giftCard.amount}
                </p>
              </div>
              {giftCard.serviceType && (
                <div>
                  <p className="text-sm text-gray-600">Service</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {giftCard.serviceType}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">From</span>
                <span className="font-medium">{customization.senderName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">To</span>
                <span className="font-medium">{customization.recipientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery Date</span>
                <span className="font-medium">
                  {new Date(customization.deliveryDate).toLocaleDateString()}
                </span>
              </div>
            </div>

            {customization.personalMessage && (
              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-2">Personal Message</p>
                <p className="text-gray-800 italic">
                  "{customization.personalMessage}"
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Confirmation */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">
                  Confirmation Emails Sent
                </h3>
                <p className="text-sm text-blue-800">
                  A confirmation has been sent to <strong>{customization.senderEmail}</strong>.
                  The gift card will be delivered to <strong>{customization.recipientEmail}</strong> on{" "}
                  {new Date(customization.deliveryDate).toLocaleDateString()}.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Find Clinics CTA */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Find Participating Massage Clinics
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Your recipient can use this gift card at any of our participating massage clinics.
                </p>
                <Button
                  onClick={() => navigate("/gift-cards/clinics")}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  View Participating Clinics
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            onClick={() => navigate("/gift-cards")}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Gift className="w-5 h-5 mr-2" />
            Buy Another Gift Card
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate("/")}
          >
            Back to Home
          </Button>
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Questions? Contact us at{" "}
            <a href="mailto:info@bookatherapy.com" className="text-emerald-600 hover:underline">
              info@bookatherapy.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
